/**
 * @since 2026-04-18
 * @author Francesco Parrella
 * @maintainer Francesco Parrella
 * @copyright AlgoTraders, All rights reserved
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import ButtonBlue from "components/Shared/ButtonBlue";
import CheckPanelRegistry from "components/Checks/registry";
import IssueList from "components/Shared/IssueList";
import { useNotification } from "components/Shared/Notifications";
import OverrideDialog from "components/Shared/OverrideDialog";
import OverrideList from "components/Shared/OverrideList";
import StatusText from "components/Shared/StatusText";
import { CHECK_SUPPORTS, CHECK_THRESHOLD_OPTIONS } from "components/Shared/overrideConfig";
import {
  createOverride,
  deactivateOverride,
  getChecksCatalog,
  getSecurity,
  listOverrides,
  startFixJob,
  unvalidateIssue,
  validateIssue,
} from "services/api";
import "./PanelSecurityDetail.scss";

const WRITE_ROLES = new Set(["PM", "OPERATOR"]);

// Display-only relabel of the daily bar (raw value "1Day" is kept for routing/keys).
const formatBarSize = (barSize) => (barSize === "1Day" ? "1Day_EndOfDay" : barSize || "");

const emptyDialogState = { open: false, kind: null, checkName: null, prefill: null };

const isOpenInNewTabClick = (e) =>
  e && (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1);

const PanelSecurityDetail = ({
  securityType,
  securityName,
  barSize,
  idToken,
  getToken,
  userRole,
  initialCheckName = null,
  onBack,
  onApiError,
  fixJobCompletionTick = 0,
}) => {
  const [payload, setPayload] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(emptyDialogState);
  const [revealedOverrides, setRevealedOverrides] = useState(() => new Set());
  const { notifyError, notifyInfo, notifySuccess } = useNotification();

  const runFixJobForThisSecurity = useCallback(async (reason) => {
    try {
      await startFixJob(
        {
          scope: [{ securityName, barSize }],
          reason: reason || `Auto-run for ${securityName}/${barSize}`,
        },
        idToken,
        getToken,
      );
      notifyInfo("Fix job queued");
    } catch (e) {
      notifyError(e?.message || "Failed to start fix job");
    }
  }, [securityName, barSize, idToken, getToken, notifyError, notifyInfo]);

  const revealOverrides = useCallback((checkName) => {
    setRevealedOverrides((prev) => {
      const next = new Set(prev);
      next.add(checkName);
      return next;
    });
  }, []);

  const canValidate = useMemo(
    () => WRITE_ROLES.has((userRole || "").toUpperCase()),
    [userRole],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [security, catalogResponse, overridesResp] = await Promise.all([
          getSecurity(securityName, barSize, idToken, getToken),
          getChecksCatalog(idToken, getToken),
          listOverrides({ securityName, barSize }, idToken, getToken),
        ]);
        if (cancelled) return;
        setPayload(security);
        setCatalog(catalogResponse.checks || []);
        setOverrides(overridesResp.overrides || []);
      } catch (e) {
        if (!cancelled) onApiError?.(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [securityName, barSize, idToken, getToken, onApiError, fixJobCompletionTick]);

  const scrolledForCheckRef = useRef(null);
  useEffect(() => {
    if (loading || !payload || !initialCheckName) return;
    if (scrolledForCheckRef.current === initialCheckName) return;
    scrolledForCheckRef.current = initialCheckName;
    const raf = requestAnimationFrame(() => {
      const el = document.getElementById(`check-${initialCheckName}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => cancelAnimationFrame(raf);
  }, [loading, payload, initialCheckName]);

  const updateIssueValidation = useCallback((checkName, issueId, validation) => {
    setPayload((prev) => {
      if (!prev) return prev;
      const checkDetails = prev.checkDetails || {};
      const detail = checkDetails[checkName];
      if (!detail || !Array.isArray(detail.issues)) return prev;
      const nextIssues = detail.issues.map((i) =>
        i.issueId === issueId ? { ...i, validation } : i,
      );
      const validatedCount = nextIssues.filter((i) => i.validation).length;
      const prevSummary = (prev.checkSummaries || {})[checkName] || {};
      return {
        ...prev,
        checkDetails: {
          ...checkDetails,
          [checkName]: { ...detail, issues: nextIssues },
        },
        checkSummaries: {
          ...(prev.checkSummaries || {}),
          [checkName]: { ...prevSummary, validatedCount },
        },
      };
    });
  }, []);

  const handleValidated = useCallback((checkName) => async (issue, reason, andRun = false) => {
    const optimistic = { validatedBy: "…", validatedAt: "", reason: reason || "" };
    updateIssueValidation(checkName, issue.issueId, optimistic);
    try {
      const response = await validateIssue(
        { issueId: issue.issueId, securityName, barSize, checkName, reason: reason || "" },
        idToken,
        getToken,
      );
      updateIssueValidation(checkName, issue.issueId, response.validation);
      notifySuccess("Issue validated");
      if (andRun) {
        await runFixJobForThisSecurity(`Auto-run after validating issue on ${checkName}`);
      }
    } catch (e) {
      updateIssueValidation(checkName, issue.issueId, null);
      notifyError(e?.message || "Failed to validate issue");
    }
  }, [securityName, barSize, idToken, getToken, notifyError, notifySuccess, updateIssueValidation, runFixJobForThisSecurity]);

  const handleUnvalidated = useCallback((checkName) => async (issueId) => {
    const prevValidation = (payload?.checkDetails?.[checkName]?.issues || [])
      .find((i) => i.issueId === issueId)?.validation || null;
    updateIssueValidation(checkName, issueId, null);
    try {
      await unvalidateIssue(issueId, idToken, getToken);
      notifySuccess("Validation removed");
    } catch (e) {
      updateIssueValidation(checkName, issueId, prevValidation);
      notifyError(e?.message || "Failed to un-validate issue");
    }
  }, [payload, idToken, getToken, notifyError, notifySuccess, updateIssueValidation]);

  const openDialog = useCallback((kind, checkName = null, prefill = null) => {
    setDialog({ open: true, kind, checkName, prefill });
  }, []);

  const handleCreateOverride = useCallback(async (overridePayload, andRun = false) => {
    try {
      await createOverride(overridePayload, idToken, getToken);
      notifySuccess("Override saved");
      setDialog(emptyDialogState);
      const overridesResp = await listOverrides({ securityName, barSize }, idToken, getToken);
      setOverrides(overridesResp.overrides || []);
      if (andRun) {
        await runFixJobForThisSecurity(`Auto-run after saving ${overridePayload.kind} override`);
      }
    } catch (e) {
      notifyError(e?.message || "Failed to save override");
    }
  }, [idToken, getToken, securityName, barSize, notifyError, notifySuccess, runFixJobForThisSecurity]);

  const handleDeactivateOverride = useCallback(async (overrideId) => {
    try {
      await deactivateOverride(overrideId, idToken, getToken);
      notifySuccess("Override removed");
      setOverrides((prev) => prev.filter((o) => o.overrideId !== overrideId));
    } catch (e) {
      notifyError(e?.message || "Failed to remove override");
    }
  }, [idToken, getToken, notifyError, notifySuccess]);

  const overridesByCheck = useMemo(() => {
    const groups = {};
    const startDateOverrides = [];
    for (const o of overrides) {
      if (o.kind === "startDate") {
        startDateOverrides.push(o);
      } else if (o.checkName) {
        (groups[o.checkName] = groups[o.checkName] || []).push(o);
      }
    }
    return { groups, startDateOverrides };
  }, [overrides]);

  const SEVERITY_RANK = { ok: 0, info: 0, na: 0, fullyValidated: 1, overridden: 1, warn: 2, error: 3 };

  if (loading) {
    return (
      <Box className="panelSecurityDetail">
        <Box className="panelSecurityDetail__loading">Loading...</Box>
      </Box>
    );
  }

  if (!payload) {
    return (
      <Box className="panelSecurityDetail">
        <Box className="panelSecurityDetail__empty">No data available.</Box>
      </Box>
    );
  }

  const applicableChecks = catalog.filter((check) => {
    if (check.appliesToSecurityTypes && !check.appliesToSecurityTypes.includes(securityType)) return false;
    if (check.appliesToBarSizes && !check.appliesToBarSizes.includes(barSize)) return false;
    return Boolean((payload.checkDetails || {})[check.name]);
  });

  const activeCheckDialog = dialog.open && dialog.kind !== "startDate" ? dialog.checkName : null;
  const activeCheckInfo = activeCheckDialog
    ? catalog.find((c) => c.name === activeCheckDialog)
    : null;

  const checkHasActiveOverride = (checkName) => {
    const ownOverrides = overridesByCheck.groups[checkName];
    if (ownOverrides && ownOverrides.length > 0) return true;
    if (checkName === "GapCalendar" && overridesByCheck.startDateOverrides.length > 0) return true;
    return false;
  };

  const checksWithSeverity = applicableChecks.map((check) => {
    const summary = (payload.checkSummaries || {})[check.name];
    const detail = (payload.checkDetails || {})[check.name];
    const issues = (detail && detail.issues) || [];
    const vCount = summary?.validatedCount || 0;
    const raw = summary?.acknowledged ? "ok" : (summary?.severity || "ok");
    const normalized = raw === "info" ? "ok" : raw;
    const hasOverride = !summary?.acknowledged && checkHasActiveOverride(check.name);
    const overrideSup = hasOverride && normalized !== "ok" && normalized !== "na";
    const fullVal = !summary?.acknowledged && !overrideSup
      && issues.length > 0 && vCount >= issues.length;
    const effective = overrideSup
      ? "overridden"
      : (fullVal ? "fullyValidated" : normalized);
    return { check, severity: effective };
  });

  const overallSectionSeverity = checksWithSeverity.reduce(
    (acc, { severity }) => ((SEVERITY_RANK[severity] ?? 0) > (SEVERITY_RANK[acc] ?? 0) ? severity : acc),
    "ok"
  );

  const issueSections = checksWithSeverity.filter(
    ({ severity }) => severity === "warn" || severity === "error"
  );

  const handleIssueLinkClick = (e, checkName) => {
    if (isOpenInNewTabClick(e)) return;
    e.preventDefault();
    const target = document.getElementById(`check-${checkName}`);
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
      const hash = `#${encodeURIComponent(checkName)}`;
      if (window.location.hash !== hash) window.history.replaceState({}, "", hash);
    }
  };

  return (
    <Box className="panelSecurityDetail">
      <Box className="panelSecurityDetail__titleRow">
        <Typography variant="h4" className="panelSecurityDetail__name">{securityName}</Typography>
        <Typography variant="body1" className="panelSecurityDetail__meta">
          {securityType} · {formatBarSize(barSize)}
        </Typography>
        <StatusText severity={overallSectionSeverity} size="large" />
      </Box>

      {issueSections.length > 0 && (
        <Box className="panelSecurityDetail__issuesNav">
          <Typography variant="caption" className="panelSecurityDetail__issuesNavLabel">
            Issues:
          </Typography>
          {issueSections.map(({ check, severity }) => (
            <a
              key={check.name}
              href={`#${encodeURIComponent(check.name)}`}
              className={`panelSecurityDetail__issueLink panelSecurityDetail__issueLink--${severity}`}
              onClick={(e) => handleIssueLinkClick(e, check.name)}
            >
              {check.displayName || check.name}
            </a>
          ))}
        </Box>
      )}

      {applicableChecks.length === 0 && (
        <Box className="panelSecurityDetail__empty">
          No checks produced output for this security.
        </Box>
      )}

      {applicableChecks.map((check) => {
        const detail = (payload.checkDetails || {})[check.name];
        const summary = (payload.checkSummaries || {})[check.name];
        const issues = (detail && detail.issues) || [];
        const validatedCount = summary?.validatedCount || 0;
        const Panel = CheckPanelRegistry[check.name];
        const supports = CHECK_SUPPORTS[check.name] || {};
        const isGapCalendar = check.name === "GapCalendar";
        const checkOverrides = isGapCalendar
          ? [...(overridesByCheck.groups[check.name] || []), ...overridesByCheck.startDateOverrides]
          : (overridesByCheck.groups[check.name] || []);
        const rawSeverity = summary?.acknowledged ? "ok" : (summary?.severity || "ok");
        const normalizedSeverity = rawSeverity === "info" ? "ok" : rawSeverity;
        const hasOverride = !summary?.acknowledged && checkHasActiveOverride(check.name);
        const overrideSuppresses = hasOverride && normalizedSeverity !== "ok" && normalizedSeverity !== "na";
        const fullyValidated = !summary?.acknowledged && !overrideSuppresses
          && issues.length > 0 && validatedCount >= issues.length;
        const sectionSeverity = overrideSuppresses
          ? "overridden"
          : (fullyValidated ? "fullyValidated" : normalizedSeverity);
        const checkClassName = [
          "panelSecurityDetail__check",
          `panelSecurityDetail__check--${sectionSeverity}`,
          initialCheckName === check.name ? "panelSecurityDetail__check--target" : "",
        ].filter(Boolean).join(" ");
        return (
          <Box
            key={check.name}
            id={`check-${check.name}`}
            className={checkClassName}
          >
            <Box className="panelSecurityDetail__checkHeader">
              <Typography variant="h6">{check.displayName || check.name}</Typography>
              {summary?.acknowledged && summary?.originalSeverity ? (
                <StatusText
                  severity="ok"
                  label="OK"
                  suffix={` (was ${summary.originalSeverity})`}
                />
              ) : overrideSuppresses ? (
                <StatusText
                  severity="overridden"
                  suffix={` (was ${summary?.severity})`}
                />
              ) : fullyValidated ? (
                <StatusText
                  severity="fullyValidated"
                  suffix={normalizedSeverity !== "ok" ? ` (was ${summary?.severity})` : undefined}
                />
              ) : (
                <StatusText severity={summary?.severity || "ok"} />
              )}
              {validatedCount > 0 && !fullyValidated && (
                <StatusText severity="validated" label={`${validatedCount} validated`} />
              )}
            </Box>
            {check.description && (
              <Typography variant="body2" className="panelSecurityDetail__checkDescription">
                {check.description}
              </Typography>
            )}
            {canValidate && checkOverrides.length === 0 && (() => {
              const hasAnyOverrideAction = supports.threshold || supports.dateRangeException
                || supports.acknowledgment || isGapCalendar;
              if (!hasAnyOverrideAction) {
                return null;
              }
              const isPassing = sectionSeverity === "ok" || sectionSeverity === "na"
                || sectionSeverity === "overridden" || sectionSeverity === "fullyValidated";
              const showButtons = !isPassing || revealedOverrides.has(check.name);
              if (!showButtons) {
                return (
                  <Box className="panelSecurityDetail__overrideActions">
                    <Tooltip arrow title="This check is passing. Click to reveal the override and acknowledge actions anyway.">
                      <Button
                        size="small"
                        className="panelSecurityDetail__revealButton"
                        onClick={() => revealOverrides(check.name)}
                      >
                        Show validation options
                      </Button>
                    </Tooltip>
                  </Box>
                );
              }
              return (
                <Box className="panelSecurityDetail__overrideActions">
                  {supports.threshold && (
                    <Tooltip arrow title="Loosen or tighten this check's sensitivity for this security. Affects which items get flagged going forward.">
                      <Button
                        size="small"
                        className="panelSecurityDetail__overrideButton"
                        onClick={() => openDialog("threshold", check.name)}
                      >
                        Override threshold
                      </Button>
                    </Tooltip>
                  )}
                  {supports.dateRangeException && (
                    <Tooltip arrow title="Ignore issues from this check within a specific date range (e.g. a known corporate action or market event).">
                      <Button
                        size="small"
                        className="panelSecurityDetail__overrideButton"
                        onClick={() => openDialog("dateRangeException", check.name)}
                      >
                        Add exception window
                      </Button>
                    </Tooltip>
                  )}
                  {supports.acknowledgment && (
                    <Tooltip arrow title="Accept this check's current status as reviewed. Clears the warning without changing thresholds or data.">
                      <Button
                        size="small"
                        className="panelSecurityDetail__overrideButton"
                        onClick={() => openDialog("acknowledgment", check.name)}
                      >
                        Acknowledge
                      </Button>
                    </Tooltip>
                  )}
                  {isGapCalendar && (
                    <Tooltip arrow title="Trim the security's usable history. Bars before this date are excluded from all checks.">
                      <Button
                        size="small"
                        className="panelSecurityDetail__overrideButton"
                        onClick={() => openDialog("startDate", null)}
                      >
                        {overridesByCheck.startDateOverrides.length > 0
                          ? `Start date: ${overridesByCheck.startDateOverrides[0].value}`
                          : "Set start date"}
                      </Button>
                    </Tooltip>
                  )}
                </Box>
              );
            })()}
            {Panel ? (
              <Panel
                detail={detail}
                summary={summary}
                security={payload}
                onAcknowledgeBar={
                  canValidate && supports.dateRangeException
                    ? (date) => openDialog("dateRangeException", check.name, {
                        startDate: date,
                        endDate: date,
                      })
                    : undefined
                }
              />
            ) : (
              <Box className="panelSecurityDetail__missing">
                Panel &quot;{check.name}&quot; is not registered in the frontend.
              </Box>
            )}
            <IssueList
              issues={issues}
              canValidate={canValidate}
              onValidated={handleValidated(check.name)}
              onUnvalidated={handleUnvalidated(check.name)}
            />
            <OverrideList
              overrides={checkOverrides}
              canWrite={canValidate}
              onDeactivate={handleDeactivateOverride}
            />
          </Box>
        );
      })}

      <Box className="panelSecurityDetail__backRow">
        <ButtonBlue alignment="center" onClick={onBack} label="Back" />
      </Box>

      <OverrideDialog
        open={dialog.open}
        kind={dialog.kind}
        securityName={securityName}
        barSize={barSize}
        checkName={dialog.checkName}
        checkDisplayName={activeCheckInfo?.displayName}
        thresholdOptions={dialog.kind === "threshold" ? (CHECK_THRESHOLD_OPTIONS[dialog.checkName] || []) : []}
        prefillStartDate={dialog.prefill?.startDate}
        prefillEndDate={dialog.prefill?.endDate}
        onConfirm={handleCreateOverride}
        onCancel={() => setDialog(emptyDialogState)}
      />
    </Box>
  );
};

export default PanelSecurityDetail;
